import React from 'react'
import { useTranslation } from 'react-i18next'
import { Archive, Building2, List } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import type { ParcelsModuleConfig } from '@/types/parcels'
import type { ModuleFieldComponentProps } from '@/types/space-wizard'
import type { Space } from '@/types/spaces'
import { RetentionField } from '@/components/features/spaces/retention'
import { RETENTION_DEFAULT_MONTHS, pendingShortening } from '@/components/features/spaces/retentionUtils'
import { DEFAULT_REMINDER_DAYS, type ParcelsConfigErrors } from './parcelUtils'

interface ToggleProps {
  id: string
  label: string
  hint: string
  checked: boolean
  disabled?: boolean
  onChange: (value: boolean) => void
}

const Toggle = ({ id, label, hint, checked, disabled, onChange }: ToggleProps) => (
  <div className="flex items-start justify-between gap-4">
    <div className="min-w-0 space-y-1">
      <label htmlFor={id} className="text-sm font-medium text-gray-700">{label}</label>
      <p className="text-xs text-gray-500">{hint}</p>
    </div>
    <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={onChange} aria-label={label} className="data-[state=checked]:bg-primary" />
  </div>
)

/**
 * The parcels setup (F-81 section 6.2): where a parcel is kept, whether the e-mail carries the photo
 * and who confirms a pickup. Short, so the wizard asks it on the details step (PADROES-UI section 14)
 * and the space edit draws this very component.
 */
interface Props extends ModuleFieldComponentProps<ParcelsModuleConfig> {
  errors?: ParcelsConfigErrors | null
  /** The space edit adds the retention period (F-203a); the wizard keeps the default. */
  context?: 'create' | 'edit'
  space?: Space
}

export const ParcelsSpaceFields: React.FC<Props> = ({ config, onChange, disabled = false, errors, context, space }) => {
  const { t } = useTranslation('parcels')
  const lockerMode = config.lockerMode === 'free' || config.lockerMode === 'list' ? config.lockerMode : 'none'
  const remindersOn = config.reminderAfterDays !== null && config.reminderAfterDays !== undefined
  const options = [
    { value: 'none' as const, icon: Building2, label: t('config.lockerMode.none') },
    { value: 'list' as const, icon: List, label: t('config.lockerMode.list') },
    { value: 'free' as const, icon: Archive, label: t('config.lockerMode.free') }
  ]

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">{t('config.lockerMode.label')}</label>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('config.lockerMode.label')}>
          {options.map(({ value, icon: Icon, label }) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={lockerMode === value}
              disabled={disabled}
              onClick={() => onChange(value === 'none' ? { ...config, lockerMode: value, lockerPickupCode: false } : { ...config, lockerMode: value })}
              className={cn(
                'flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm transition-colors',
                lockerMode === value ? 'bg-primary-100 text-primary-700' : 'hover:bg-gray-100',
                disabled && 'cursor-not-allowed opacity-50'
              )}
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              <span>{label}</span>
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500">{t(`config.lockerMode.${lockerMode}Hint`)}</p>
      </div>

      {lockerMode !== 'none' && (
        <Toggle
          id="parcels-locker-code"
          label={t('config.lockerPickupCode.label')}
          hint={t('config.lockerPickupCode.hint')}
          checked={config.lockerPickupCode === true}
          disabled={disabled}
          onChange={(value) => onChange({ ...config, lockerPickupCode: value })}
        />
      )}

      <Toggle
        id="parcels-email-photo"
        label={t('config.emailIncludesPhoto.label')}
        hint={t('config.emailIncludesPhoto.hint')}
        checked={config.emailIncludesPhoto !== false}
        disabled={disabled}
        onChange={(value) => onChange({ ...config, emailIncludesPhoto: value })}
      />

      <Toggle
        id="parcels-staff-confirmation"
        label={t('config.requireStaffConfirmation.label')}
        hint={config.requireStaffConfirmation ? t('config.requireStaffConfirmation.onHint') : t('config.requireStaffConfirmation.offHint')}
        checked={config.requireStaffConfirmation === true}
        disabled={disabled}
        onChange={(value) => onChange({ ...config, requireStaffConfirmation: value })}
      />

      <div className="space-y-2">
        <label htmlFor="parcels-reminder-days" className="text-sm font-medium text-gray-700">{t('config.reminder.afterDays.label')}</label>
        <Input
          id="parcels-reminder-days"
          type="number"
          inputMode="numeric"
          min={1}
          max={60}
          value={config.reminderAfterDays ?? ''}
          disabled={disabled}
          placeholder={t('config.reminder.afterDays.placeholder')}
          onChange={(e) => {
            const raw = e.target.value
            onChange({ ...config, reminderAfterDays: raw === '' ? null : Number(raw) })
          }}
          className={cn('max-w-[12rem]', errors?.reminderAfterDays && 'border-red-500')}
          aria-invalid={Boolean(errors?.reminderAfterDays)}
        />
        {errors?.reminderAfterDays
          ? <p role="alert" className="text-xs text-red-600">{errors.reminderAfterDays}</p>
          : <p className="text-xs text-gray-500">{remindersOn ? t('config.reminder.afterDays.hint', { count: config.reminderAfterDays ?? DEFAULT_REMINDER_DAYS }) : t('config.reminder.afterDays.offHint')}</p>}
      </div>

      {remindersOn && (
        <div className="space-y-2">
          <label htmlFor="parcels-reminder-count" className="text-sm font-medium text-gray-700">{t('config.reminder.maxCount.label')}</label>
          <Input
            id="parcels-reminder-count"
            type="number"
            inputMode="numeric"
            min={1}
            max={5}
            value={config.reminderMaxCount ?? ''}
            disabled={disabled}
            onChange={(e) => onChange({ ...config, reminderMaxCount: Number(e.target.value) })}
            className={cn('max-w-[12rem]', errors?.reminderMaxCount && 'border-red-500')}
            aria-invalid={Boolean(errors?.reminderMaxCount)}
          />
          {errors?.reminderMaxCount
            ? <p role="alert" className="text-xs text-red-600">{errors.reminderMaxCount}</p>
            : <p className="text-xs text-gray-500">{t('config.reminder.maxCount.hint')}</p>}
        </div>
      )}

      {context === 'edit' && (
        <RetentionField
          module="parcels"
          value={config.retentionMonths ?? RETENTION_DEFAULT_MONTHS}
          pending={pendingShortening(space, 'parcels')}
          onChange={(months) => onChange({ ...config, retentionMonths: months })}
          error={errors?.retentionMonths}
          disabled={disabled}
        />
      )}

      <p className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600">{t('config.privacy')}</p>
    </div>
  )
}
